import React, { useState, useRef, useEffect } from 'react';
import { Plus, X } from 'lucide-react';

interface Workspace {
  id: string;
  name: string;
}

interface TabsProps {
  workspaces: Workspace[];
  activeId: string;
  onSelect: (id: string) => void;
  onAdd: () => void;
  onClose: (id: string, e: React.MouseEvent) => void;
  onRename: (id: string, newName: string) => void;
}

export function Tabs({ workspaces, activeId, onSelect, onAdd, onClose, onRename }: TabsProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editingId && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingId]);

  const handleDoubleClick = (id: string, name: string) => {
    setEditingId(id);
    setEditName(name);
  };

  const handleKeyDown = (e: React.KeyboardEvent, id: string) => {
    if (e.key === 'Enter') {
      submitRename(id);
    } else if (e.key === 'Escape') {
      setEditingId(null);
    }
  };

  const submitRename = (id: string) => {
    if (editName.trim()) {
      onRename(id, editName.trim());
    }
    setEditingId(null);
  };

  return (
    <nav role="tablist" aria-label="Workspaces" className="flex items-end gap-1.5 px-4 pt-3 flex-1 overflow-x-auto overflow-y-hidden no-scrollbar">
      {workspaces.map((ws) => (
        <div
          role="tab"
          aria-selected={activeId === ws.id}
          key={ws.id}
          onClick={() => onSelect(ws.id)}
          onDoubleClick={() => handleDoubleClick(ws.id, ws.name)}
          className={`group flex items-center gap-2 px-3 py-1.5 rounded-t-lg border border-b-0 cursor-pointer max-w-[200px] shrink-0 mb-[-1px] ${
            activeId === ws.id 
              ? 'bg-white dark:bg-slate-950 border-slate-300 dark:border-slate-800 text-emerald-600 dark:text-emerald-400 font-semibold dark:shadow-[0_-2px_6px_-2px_rgba(0,0,0,0.05)]' 
              : 'bg-slate-200 dark:bg-slate-800/50 border-transparent text-slate-500 hover:bg-slate-200/70 hover:text-slate-700 dark:hover:bg-slate-800/80 dark:hover:text-slate-300'
          }`}
        >
          {editingId === ws.id ? (
            <input
              ref={inputRef}
              type="text"
              aria-label="Workspace name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onBlur={() => submitRename(ws.id)}
              onKeyDown={(e) => handleKeyDown(e, ws.id)}
              className="text-xs bg-transparent border-none outline-none w-24 text-emerald-700 dark:text-emerald-400 font-semibold"
            />
          ) : (
            <span className="text-xs truncate select-none" title="Double click to rename">{ws.name}</span>
          )}
          
          {workspaces.length > 1 && (
            <button 
              className={`p-0.5 rounded-md hover:bg-slate-300 dark:hover:bg-slate-700 cursor-pointer ${activeId === ws.id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
              onClick={(e) => onClose(ws.id, e)}
              aria-label={`Close ${ws.name} workspace`}
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      ))}
      <button
        onClick={onAdd}
        className="flex items-center justify-center p-1.5 rounded-md text-slate-500 hover:text-emerald-600 hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors shrink-0 mb-1 cursor-pointer"
        aria-label="New Workspace"
        title="New Workspace"
      >
        <Plus className="w-4 h-4" />
      </button>
    </nav>
  );
}
